import React, { useEffect, useState } from "react";
import { Card, useMediaQuery } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  AutocompleteInput,
  BooleanField,
  CreateButton,
  Datagrid,
  FilterButton,
  ListContextProvider,
  ListToolbar,
  RefreshButton,
  TextField,
  Title,
  TopToolbar,
  useListContext,
} from "react-admin";

import { Pagination } from "../../../components/Pagination";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { SearchField } from "../../../components/common/SearchField";
import { HelpButton } from "../../../components/common/HelpButton";

import { useSearchableList } from "../../../dataProvider/useSearchableList";
import { HelpTopicOptions } from "../../../utils/constants";

const useStyles = makeStyles(() => ({
  searchField: {
    marginTop: "0px",
    marginBottom: "8px",
    maxWidth: "16rem",
    minWidth: "16rem",
  },
}));

const HelpSearchField = ({ label, alwaysOn }) => {
  const classes = useStyles();
  const listContext = useListContext();
  const { displayedFilters, filterValues, setFilters } = listContext;

  const handleSearch = (value) => {
    if (value) {
      delete filterValues.or;
      const filters = {
        ...filterValues,
        or: [
          { name: { matchPhrasePrefix: value } },
          { name: { wildcard: `*${value}*` } },
          { url: { matchPhrasePrefix: value } },
          { url: { wildcard: `*${value}*` } },
          { topic: { matchPhrasePrefix: value } },
          { topic: { wildcard: `*${value}*` } },
        ],
      };
      setFilters(filters, displayedFilters);
    } else {
      delete filterValues.or;
      setFilters(filterValues, displayedFilters);
    }
  };

  return (
    <SearchField
      id="searchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      className={classes.searchField}
      handleSearch={handleSearch}
    />
  );
};

const getListFilters = () => [
  <AutocompleteInput
    source="topic.eq"
    label="Topic"
    key="topic-filter"
    variant="outlined"
    resettable
    choices={HelpTopicOptions}
    alwaysOn
  />,
  <BooleanField
    source="isLive.eq"
    label="Is Live"
    key="isLive-filter"
    variant="outlined"
    resettable
    optionValue="id"
    alwaysOn
  />,
  <HelpSearchField label="Search" alwaysOn />,
];

const ListActions = ({ xsDown }) => (
  <TopToolbar>
    <HelpButton />
    {!xsDown && <FilterButton />}
    <CreateButton />
    <RefreshButton />
  </TopToolbar>
);

export const HelpPageList = (props) => {
  const xsDown = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const mdUp = useMediaQuery((theme) => theme.breakpoints.up("md"));

  const defaultSort = {
    field: `{"key":"helpPageByUrl", "search":"url"}`,
    order: "ASC",
  };

  const [defaultFilter] = useState({ active: "true" });
  const defaultSearchFilter = { active: { eq: "true" } };

  const sessionPerPage = localStorage.getItem(`${props.resource}_perPage`);
  const [defaultPerPage, setDefaultPerPage] = useState(
    sessionPerPage ? Number(sessionPerPage) : 10,
  );

  const listContext = useSearchableList({
    ...props,
    defaultTitle: "Help Pages",
    defaultFilter,
    defaultSort,
    defaultSearchFilter,
    defaultPerPage,
    resource: "helpPages",
  });

  useEffect(() => {
    if (listContext?.perPage) {
      localStorage.setItem(`${props.resource}_perPage`, listContext.perPage);
      setDefaultPerPage(Number(listContext.perPage));
    }
  }, [listContext.perPage, props.resource]);

  useEffect(() => {
    if (listContext?.filter) {
      const fieldObj = JSON.parse(defaultSort.field);
      const keyToCheck = fieldObj.key;
      const hasKey = keyToCheck in listContext.filter;
      const hasFilterValues = Object.keys(listContext.filterValues).length > 0;
      if (hasKey && hasFilterValues) listContext.setFilters({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listContext.filter, defaultSort.field]);

  return (
    <ListContextProvider value={listContext}>
      <Title title={<ForceTitle title="Help Pages" />} />
      <ListToolbar
        {...props}
        filters={getListFilters(mdUp)}
        actions={<ListActions xsDown={xsDown} />}
      />
      <Card>
        <Datagrid rowClick="edit">
          <TextField source="name" label="Name" sortable />
          <TextField
            source="url"
            label="URL"
            sortBy={`{"key":"helpPageByUrl", "search":"url"}`}
          />
          <TextField source="topic" label="Topic" sortable />
          <BooleanField source="isLive" label="Is Live" sortable />
        </Datagrid>
        <Pagination {...listContext} />
      </Card>
    </ListContextProvider>
  );
};
